import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  FormLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import {
  Abordagem,
  Localizacao,
  PsicologoEF,
  PsicologoEFPayload,
} from "src/apis/eezyfamily";
import CrudDialog from "src/pages/Admin/EezyFamily/components/CrudDialog";

type Props = {
  open: boolean;
  editTarget: PsicologoEF | null;
  localizacoes: Localizacao[];
  abordagens: Abordagem[];
  onClose: () => void;
  onSaved: () => void;
  onCreate: (p: PsicologoEFPayload) => Promise<PsicologoEF>;
  onUpdate: (id: number, p: Partial<PsicologoEFPayload>) => Promise<void>;
  onUploadPhoto: (id: number, file: File) => Promise<{ url: string }>;
};

const emptyForm: PsicologoEFPayload = {
  name: "",
  title: "",
  description: "",
  therapy_meaning: "",
  contact: "",
  location: "",
  approach: "",
  online_service: "",
  human: "",
  topics: [],
  is_active: true,
};

const PsicologoModal: FC<Props> = ({
  open,
  editTarget,
  localizacoes,
  abordagens,
  onClose,
  onSaved,
  onCreate,
  onUpdate,
  onUploadPhoto,
}): JSX.Element => {
  const [form, setForm] = useState<PsicologoEFPayload>(emptyForm);
  const [topicsInput, setTopicsInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState("");
  const [photoPreviewError, setPhotoPreviewError] = useState(false);
  const [pendingPhotoFile, setPendingPhotoFile] = useState<File | null>(null);
  const [error, setError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const isEdit = editTarget !== null;

  useEffect(() => {
    if (open) {
      if (editTarget) {
        const { id, photo_url, ...rest } = editTarget as PsicologoEF;
        setForm({ ...emptyForm, ...rest });
        setTopicsInput((editTarget.topics ?? []).join(", "));
        setPhotoPreviewUrl(photo_url ?? "");
      } else {
        setForm(emptyForm);
        setTopicsInput("");
        setPhotoPreviewUrl("");
      }
      setPendingPhotoFile(null);
      setPhotoPreviewError(false);
      setError("");
    }
  }, [open, editTarget]);

  const setField = <K extends keyof PsicologoEFPayload>(
    key: K,
    value: PsicologoEFPayload[K]
  ) => setForm((p) => ({ ...p, [key]: value }));

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (isEdit) {
      setUploadLoading(true);
      try {
        const { url } = await onUploadPhoto(editTarget.id, file);
        setPhotoPreviewUrl(url);
        setPhotoPreviewError(false);
      } catch (err: any) {
        setError(err?.message ?? "Erro no upload da foto.");
      } finally {
        setUploadLoading(false);
        if (fileRef.current) fileRef.current.value = "";
      }
    } else {
      setPendingPhotoFile(file);
      setPhotoPreviewUrl(URL.createObjectURL(file));
      setPhotoPreviewError(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.title.trim() || !form.description.trim()) {
      setError("Nome, Título e Descrição são obrigatórios.");
      return;
    }
    setLoading(true);
    try {
      const topics = topicsInput
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      const payload = { ...form, topics };
      if (isEdit) {
        await onUpdate(editTarget.id, payload);
      } else {
        const created = await onCreate(payload);
        if (pendingPhotoFile) {
          if (!Number.isInteger(created?.id) || Number(created?.id) <= 0) {
            setError(
              "Psicólogo criado, mas não foi possível enviar a foto (ID inválido). Edite o psicólogo e envie a foto novamente."
            );
            return;
          }
          await onUploadPhoto(created.id, pendingPhotoFile);
        }
      }
      onSaved();
    } catch (err: any) {
      setError(err?.message ?? "Erro ao salvar.");
    } finally {
      setLoading(false);
    }
  };

  const photoIsValidUrl = Boolean(photoPreviewUrl) && !photoPreviewError;

  return (
    <CrudDialog
      open={open}
      title={isEdit ? `Editar Psicólogo — ${editTarget?.name}` : "Novo Psicólogo"}
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={loading}
      maxWidth="md"
    >
      {error && (
        <Box sx={{ color: "error.main", fontSize: "0.875rem" }}>{error}</Box>
      )}

      {/* Informações Básicas */}
      <Typography variant="subtitle2" color="text.secondary">
        Informações Básicas
      </Typography>
      <Divider />
      <FormGroup>
        <FormLabel required>Nome</FormLabel>
        <TextField
          value={form.name}
          onChange={(e) => setField("name", e.target.value)}
          autoFocus
        />
      </FormGroup>
      <FormGroup>
        <FormLabel required>Título</FormLabel>
        <TextField
          value={form.title}
          onChange={(e) => setField("title", e.target.value)}
          placeholder="Ex: Psicóloga CRP 06/12345"
        />
      </FormGroup>
      <FormGroup>
        <FormLabel required>Descrição</FormLabel>
        <TextField
          value={form.description}
          onChange={(e) => setField("description", e.target.value)}
          multiline
          minRows={3}
        />
      </FormGroup>
      <FormGroup>
        <FormLabel>Significado da Terapia</FormLabel>
        <TextField
          value={form.therapy_meaning}
          onChange={(e) => setField("therapy_meaning", e.target.value)}
          multiline
          minRows={2}
        />
      </FormGroup>

      {/* Localização e Contato */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
        Localização e Contato
      </Typography>
      <Divider />
      <FormGroup>
        <FormLabel>Contato (WhatsApp)</FormLabel>
        <TextField
          value={form.contact}
          onChange={(e) => setField("contact", e.target.value)}
          placeholder="Ex: (11) 99999-9999"
        />
      </FormGroup>
      <Box sx={{ display: "flex", gap: 2 }}>
        <FormGroup sx={{ flex: 1 }}>
          <FormLabel>Localização</FormLabel>
          <Select
            value={form.location ?? ""}
            onChange={(e) => setField("location", e.target.value)}
            displayEmpty
          >
            <MenuItem value="">Selecionar...</MenuItem>
            {localizacoes.map((loc) => (
              <MenuItem key={loc.id} value={loc.city}>
                {loc.city} — {loc.state}
              </MenuItem>
            ))}
          </Select>
        </FormGroup>
        <FormGroup sx={{ flex: 1 }}>
          <FormLabel>Abordagem</FormLabel>
          <Select
            value={form.approach ?? ""}
            onChange={(e) => setField("approach", e.target.value)}
            displayEmpty
          >
            <MenuItem value="">Selecionar...</MenuItem>
            {abordagens.map((a) => (
              <MenuItem key={a.id} value={a.approach}>
                {a.approach}
              </MenuItem>
            ))}
          </Select>
        </FormGroup>
      </Box>
      <Box sx={{ display: "flex", gap: 2 }}>
        <FormGroup sx={{ flex: 1 }}>
          <FormLabel>Atendimento Online</FormLabel>
          <TextField
            value={form.online_service}
            onChange={(e) => setField("online_service", e.target.value)}
            placeholder="Ex: Sim, Não, Híbrido"
          />
        </FormGroup>
        <FormGroup sx={{ flex: 1 }}>
          <FormLabel>Humano</FormLabel>
          <TextField
            value={form.human}
            onChange={(e) => setField("human", e.target.value)}
            placeholder="Descrição humanizada"
          />
        </FormGroup>
      </Box>

      {/* Foto */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
        Foto
      </Typography>
      <Divider />
      <FormGroup>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => fileRef.current?.click()}
            disabled={uploadLoading}
            sx={{ whiteSpace: "nowrap" }}
          >
            {uploadLoading ? <CircularProgress size="1rem" /> : "Upload de Foto"}
          </Button>
          {!isEdit && pendingPhotoFile && (
            <Typography variant="caption" color="text.secondary">
              {pendingPhotoFile.name} — será enviada ao salvar
            </Typography>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </Box>
      </FormGroup>
      {photoIsValidUrl && (
        <Box>
          <img
            src={photoPreviewUrl}
            alt="Preview"
            onError={() => setPhotoPreviewError(true)}
            style={{ maxHeight: 120, borderRadius: 4, objectFit: "cover" }}
          />
        </Box>
      )}

      {/* Tópicos */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
        Tópicos
      </Typography>
      <Divider />
      <FormGroup>
        <FormLabel>Tópicos</FormLabel>
        <TextField
          value={topicsInput}
          onChange={(e) => setTopicsInput(e.target.value)}
          placeholder="Ex: Ansiedade, Depressão, Relacionamentos"
          helperText="Separados por vírgula"
        />
      </FormGroup>

      {/* Status */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
        Status
      </Typography>
      <Divider />
      <FormControlLabel
        control={
          <Checkbox
            checked={Boolean(form.is_active)}
            onChange={(e) => setField("is_active", e.target.checked)}
          />
        }
        label="Agenda aberta? (Psicólogo ativo)"
      />
    </CrudDialog>
  );
};

export default PsicologoModal;
